import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { usePlaybackSession } from "@/playback/PlaybackSession";
import { useTerminatePlaybackOnAuthSessionChange } from "@/playback/authPlaybackLifecycle";
import {
  selectPlaybackQueueItem,
  shouldReattachPlaybackQueue
} from "@/playback/playbackQueueActivation";

import type {
  CastControllerProviderProps,
  CastControllerState,
  CastPlaybackQueue,
  CastState
} from "./CastController.types";
import {
  buildCastQueue,
  CAST_CONTROL_NAMESPACE,
  CAST_PRELOAD_TIME_SECONDS,
  type CastSenderDevice
} from "./CastQueue";

const CastControllerContext = createContext<CastControllerState | null>(null);
const CAST_SENDER_SDK =
  "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
// Registered in apps/viewer/.env.production and used by the native config.
const CAST_RECEIVER_APP_ID =
  process.env.EXPO_PUBLIC_CAST_RECEIVER_APP_ID ?? "241F0BAC";

type CafWindow = Window & {
  __onGCastApiAvailable?: (available: boolean, errorInfo?: unknown) => void;
  cast?: any;
  chrome?: any;
};

let sdkPromise: Promise<CafWindow> | null = null;

function isSupportedBrowser() {
  const agent = window.navigator.userAgent;
  return /Chrome\//.test(agent) || /Edg\//.test(agent);
}

function webSenderDevice(): CastSenderDevice {
  const navigatorWithPlatform = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const platform =
    navigatorWithPlatform.userAgentData?.platform ?? navigator.platform ?? "";
  return /Mac/i.test(platform) || /Macintosh/i.test(navigator.userAgent)
    ? "Mac"
    : "computer";
}

function loadCastSdk() {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<CafWindow>((resolve, reject) => {
    const castWindow = window as CafWindow;
    if (castWindow.cast?.framework && castWindow.chrome?.cast) {
      resolve(castWindow);
      return;
    }
    const previousCallback = castWindow.__onGCastApiAvailable;
    castWindow.__onGCastApiAvailable = (available, errorInfo) => {
      previousCallback?.(available, errorInfo);
      if (available && castWindow.cast?.framework && castWindow.chrome?.cast) {
        resolve(castWindow);
      } else {
        reject(new Error(`Google Cast sender unavailable: ${String(errorInfo ?? "unknown")}`));
      }
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CAST_SENDER_SDK}"]`);
    if (existing) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = CAST_SENDER_SDK;
    script.onerror = () => reject(new Error("Google Cast sender SDK failed to load"));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

function logFailure(operation: string, error: unknown) {
  console.log(
    `[cast:web] ${operation} failed: ${error instanceof Error ? error.message : String(error)}`
  );
}

function runCallback(operation: string, callback: (() => void) | undefined) {
  try {
    callback?.();
  } catch (error) {
    logFailure(operation, error);
  }
}

function invokeRemote(operation: string, request: (ok: () => void, fail: (error: unknown) => void) => void) {
  try {
    request(() => {}, (error) => logFailure(operation, error));
  } catch (error) {
    logFailure(operation, error);
  }
}

export function CastControllerProvider({ children }: CastControllerProviderProps) {
  const playbackSession = usePlaybackSession();
  const [castState, setCastState] = useState<CastState>("none");
  const [activePlayback, setActivePlaybackState] = useState<CastPlaybackQueue | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [selectedAudioTrackState, setSelectedAudioTrackState] = useState<string | null>(null);
  const preferredAudioTrackRef = useRef<string | null>(null);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [sessionGeneration, setSessionGeneration] = useState(0);
  const [mediaGeneration, setMediaGeneration] = useState(0);
  const contextRef = useRef<any>(null);
  const sessionRef = useRef<any>(null);
  const mediaRef = useRef<any>(null);
  const activePlaybackRef = useRef<CastPlaybackQueue | null>(null);
  const screenAttachedRef = useRef(false);
  const castStateRef = useRef<CastState>("none");
  const loadedQueueKeyRef = useRef<string | null>(null);
  const loadingQueueKeyRef = useRef<string | null>(null);
  const receiverIndexRef = useRef<number | null>(null);
  const currentTimeRef = useRef(0);
  const wasConnectedRef = useRef(false);
  const queueEndedKeyRef = useRef<string | null>(null);
  castStateRef.current = castState;
  currentTimeRef.current = currentTime;

  const castNowPlaying =
    castState === "connected"
      ? activePlayback?.items[activePlayback.currentIndex] ?? null
      : null;
  const currentAudioTracks = castNowPlaying?.audioTracks ?? [];
  const selectedAudioTrack = currentAudioTracks.some(
    (track) => track.id === selectedAudioTrackState
  )
    ? selectedAudioTrackState
    : currentAudioTracks.find((track) => track.isDefault)?.id ??
      currentAudioTracks[0]?.id ??
      null;
  const availableAudioTracks = currentAudioTracks.map(({ id, label }) => ({ id, label }));
  useEffect(() => {
    if (!castNowPlaying) return;
    playbackSession.updateNowPlaying({
      danceId: castNowPlaying.id,
      performanceId: castNowPlaying.performanceId,
      title: castNowPlaying.title
    });
  }, [castNowPlaying, playbackSession.updateNowPlaying]);

  const clearActivePlayback = useCallback(() => {
    activePlaybackRef.current = null;
    setActivePlaybackState(null);
    loadedQueueKeyRef.current = null;
    loadingQueueKeyRef.current = null;
    receiverIndexRef.current = null;
    queueEndedKeyRef.current = null;
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const setActivePlayback = useCallback((queue: CastPlaybackQueue | null) => {
    if (!queue) {
      screenAttachedRef.current = false;
      clearActivePlayback();
      return;
    }
    if (playbackSession.revoked) return;
    const previous = activePlaybackRef.current;
    const reattaching = shouldReattachPlaybackQueue({
      next: queue,
      previous,
      remoteActive: castStateRef.current === "connected",
      screenAttached: screenAttachedRef.current
    });
    const nextQueue = reattaching
      ? { ...queue, currentIndex: previous!.currentIndex, loadKey: previous!.loadKey }
      : queue;
    screenAttachedRef.current = true;
    activePlaybackRef.current = nextQueue;
    setActivePlaybackState(nextQueue);
    if (reattaching && previous!.currentIndex !== queue.currentIndex) {
      runCallback("reattach onItemChanged", () => queue.onItemChanged(previous!.currentIndex));
    }
  }, [clearActivePlayback, playbackSession.revoked]);

  const detachActivePlayback = useCallback(() => {
    const preserve = castStateRef.current === "connected" && activePlaybackRef.current !== null;
    screenAttachedRef.current = false;
    if (!preserve) clearActivePlayback();
    return preserve;
  }, [clearActivePlayback]);

  useEffect(() => {
    if (!isSupportedBrowser()) return;
    let cancelled = false;
    const removers: Array<() => void> = [];
    void loadCastSdk()
      .then((castWindow) => {
        if (cancelled) return;
        const framework = castWindow.cast.framework;
        const context = framework.CastContext.getInstance();
        contextRef.current = context;
        context.setOptions({
          autoJoinPolicy: castWindow.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          receiverApplicationId: CAST_RECEIVER_APP_ID,
          resumeSavedSession: true
        });

        const applyContextState = (nextState: string) => {
          if (nextState === framework.CastState.CONNECTED) setCastState("connected");
          else if (nextState === framework.CastState.CONNECTING) setCastState("connecting");
          else if (nextState === framework.CastState.NOT_CONNECTED) setCastState("available");
          else setCastState("none");
          const session = context.getCurrentSession();
          sessionRef.current = session;
          const castDevice = session?.getCastDevice?.();
          setDeviceName(castDevice?.friendlyName ?? null);
          setSessionGeneration((value) => value + 1);
        };
        const castStateListener = (event: { castState: string }) => applyContextState(event.castState);
        const sessionStateListener = (event: { session: any; sessionState: string }) => {
          sessionRef.current = event.session ?? context.getCurrentSession();
          const castDevice = sessionRef.current?.getCastDevice?.();
          setDeviceName(castDevice?.friendlyName ?? null);
          setSessionGeneration((value) => value + 1);
        };
        context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, castStateListener);
        context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateListener);
        removers.push(() =>
          context.removeEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, castStateListener)
        );
        removers.push(() =>
          context.removeEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateListener)
        );
        applyContextState(context.getCastState());
      })
      .catch((error) => logFailure("SDK initialization", error));
    return () => {
      cancelled = true;
      removers.forEach((remove) => remove());
    };
  }, []);

  useEffect(() => {
    if (castState === "connected") {
      if (!wasConnectedRef.current) {
        wasConnectedRef.current = true;
        loadedQueueKeyRef.current = null;
        loadingQueueKeyRef.current = null;
        receiverIndexRef.current = null;
        runCallback("onConnected", activePlaybackRef.current?.onConnected);
      }
      return;
    }
    if (!wasConnectedRef.current) return;
    wasConnectedRef.current = false;
    mediaRef.current = null;
    loadedQueueKeyRef.current = null;
    loadingQueueKeyRef.current = null;
    receiverIndexRef.current = null;
    const active = activePlaybackRef.current;
    if (active && screenAttachedRef.current) {
      runCallback("onDisconnected", () => active.onDisconnected(currentTimeRef.current));
    } else if (!screenAttachedRef.current) {
      clearActivePlayback();
    }
  }, [castState, clearActivePlayback]);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session?.addMessageListener) return;
    const onMessage = (_namespace: string, message: string) => {
      let payload: Record<string, unknown> | null = null;
      try {
        payload = JSON.parse(message) as Record<string, unknown>;
      } catch {
        return;
      }
      if (
        payload.type === "receiver-log" &&
        typeof payload.line === "string"
      ) {
        console.log(`[cast] rx: ${payload.line}`);
        return;
      }
      if (payload.type === "audio-track-error") {
        console.log(
          `[cast:web] receiver audio track rejected id=${
            typeof payload.audioTrackId === "string"
              ? payload.audioTrackId
              : "unknown"
          } reason=${typeof payload.reason === "string" ? payload.reason : "unknown"}`
        );
        return;
      }
      if (
        payload.type === "audio-track-resolved" &&
        typeof payload.audioTrackId === "string" &&
        typeof payload.runtimeTrackId === "number"
      ) {
        const id = payload.audioTrackId;
        const runtimeTrackId = payload.runtimeTrackId;
        console.log(
          `[cast:web] receiver audio track resolved id=${id} runtimeId=${runtimeTrackId} language=${
            typeof payload.castLanguage === "string"
              ? payload.castLanguage
              : "unknown"
          } engine=${
            typeof payload.castEngine === "string"
              ? payload.castEngine
              : "unknown"
            }`
        );
        const media = mediaRef.current ?? session.getMediaSession?.();
        const mediaApi = (window as CafWindow).chrome?.cast?.media;
        if (!media || !mediaApi) return;
        const request = new mediaApi.EditTracksInfoRequest([runtimeTrackId]);
        try {
          media.editTracksInfo(
            request,
            () => {
              const activeTrackIds = media.activeTrackIds ?? [];
              if (activeTrackIds.includes(runtimeTrackId)) {
                setSelectedAudioTrackState(id);
              }
              console.log(
                `[cast:web] audio track command id=${id} requestedRuntimeId=${runtimeTrackId} activeRuntimeIds=${
                  activeTrackIds.join(",") || "none"
                }`
              );
            },
            (error: unknown) => logFailure(`set active audio track ${id}`, error)
          );
        } catch (error) {
          logFailure(`set active audio track ${id}`, error);
        }
      }
    };
    session.addMessageListener(CAST_CONTROL_NAMESPACE, onMessage);
    return () => {
      session.removeMessageListener?.(CAST_CONTROL_NAMESPACE, onMessage);
    };
  }, [sessionGeneration]);

  const applyReceiverItem = useCallback((media: any) => {
    const queue = activePlaybackRef.current;
    if (!queue || !media) return false;
    const currentItem = media.items?.find((item: any) => item.itemId === media.currentItemId);
    const customData = currentItem?.media?.customData ?? currentItem?.customData;
    if (customData?.queueId !== queue.id || customData?.queueLoadKey !== queue.loadKey) return false;
    const nextIndex = typeof customData.queueIndex === "number"
      ? customData.queueIndex
      : queue.items.findIndex((item) => item.id === customData.danceId);
    if (nextIndex < 0 || nextIndex >= queue.items.length) return false;
    const preferredAudioTrack = queue.items[nextIndex].audioTracks.find(
      (track) => track.id === preferredAudioTrackRef.current
    );
    if (preferredAudioTrack) {
      setSelectedAudioTrackState(preferredAudioTrack.id);
    }
    loadedQueueKeyRef.current = queue.loadKey;
    const loadConfirmed = loadingQueueKeyRef.current === queue.loadKey;
    if (loadConfirmed) {
      loadingQueueKeyRef.current = null;
    }
    const receiverChanged =
      receiverIndexRef.current !== nextIndex || queue.currentIndex !== nextIndex;
    if (loadConfirmed || receiverChanged) {
      console.log(
        `[cast:web] receiver item confirmed queue=${queue.id} index=${nextIndex}`
      );
    }
    if (receiverChanged) {
      receiverIndexRef.current = nextIndex;
      const nextQueue = { ...queue, currentIndex: nextIndex };
      activePlaybackRef.current = nextQueue;
      setActivePlaybackState(nextQueue);
      if (screenAttachedRef.current && nextIndex !== queue.currentIndex) {
        runCallback("onItemChanged", () => queue.onItemChanged(nextIndex));
      }
    }
    return true;
  }, []);

  useEffect(() => {
    const session = sessionRef.current;
    const queue = activePlayback;
    if (castState !== "connected" || !session || !queue) return;
    if (queue.items.length === 0 || queue.items.some((item) => !item.sourceUrl)) return;
    const existingMedia = session.getMediaSession?.();
    if (applyReceiverItem(existingMedia)) {
      mediaRef.current = existingMedia;
      setMediaGeneration((value) => value + 1);
      return;
    }
    if (loadedQueueKeyRef.current === queue.loadKey) return;
    if (loadingQueueKeyRef.current === queue.loadKey) return;

    const castWindow = window as CafWindow;
    const mediaApi = castWindow.chrome.cast.media;
    const startTime = Math.max(0, queue.getPosition());
    const queueLoad = buildCastQueue(
      queue,
      startTime,
      selectedAudioTrack,
      webSenderDevice()
    );
    const queueItems = queueLoad.items.map((item) => {
      const mediaInfo = new mediaApi.MediaInfo(item.contentUrl, "application/x-mpegURL");
      mediaInfo.contentUrl = item.contentUrl;
      mediaInfo.duration = item.duration;
      mediaInfo.streamType = mediaApi.StreamType.BUFFERED;
      mediaInfo.hlsSegmentFormat = mediaApi.HlsSegmentFormat.FMP4;
      mediaInfo.hlsVideoSegmentFormat = mediaApi.HlsVideoSegmentFormat.FMP4;
      mediaInfo.customData = item.customData;
      const metadata = new mediaApi.GenericMediaMetadata();
      metadata.title = item.title;
      metadata.subtitle = item.subtitle;
      metadata.images = item.artworkUrl
        ? [{ url: item.artworkUrl, width: 640, height: 360 }]
        : [];
      mediaInfo.metadata = metadata;
      const queueItem = new mediaApi.QueueItem(mediaInfo);
      queueItem.autoplay = true;
      queueItem.customData = item.customData;
      queueItem.preloadTime = CAST_PRELOAD_TIME_SECONDS;
      if (item.startTime !== undefined) {
        queueItem.playbackDuration = item.duration;
        queueItem.startTime = item.startTime;
      }
      return queueItem;
    });
    const queueData = new mediaApi.QueueData();
    queueData.id = queueLoad.id;
    queueData.items = queueItems;
    queueData.name = "Recital Master";
    queueData.queueType = mediaApi.QueueType.TV_SERIES;
    queueData.repeatMode = mediaApi.RepeatMode.OFF;
    queueData.startIndex = queueLoad.startIndex;
    queueData.startTime = queueLoad.startTime;
    const request = new mediaApi.LoadRequest(queueItems[queueLoad.startIndex].media);
    request.autoplay = true;
    request.currentTime = queueLoad.startTime;
    request.queueData = queueData;
    request.customData = queueLoad.customData;

    // A resolved sender request is not the receiver's confirmed queue item.
    // MEDIA_STATUS owns that transition and advances the attached watch screen.
    loadingQueueKeyRef.current = queue.loadKey;
    setPlaying(true);
    setCurrentTime(startTime);
    setDuration(queue.items[queue.currentIndex]?.duration ?? 0);
    runCallback("onConnected before queue load", queue.onConnected);
    console.log(`[cast:web] loading receiver queue (items=${queueItems.length}, startIndex=${queue.currentIndex}, startTime=${startTime})`);
    void session.loadMedia(request).then(() => {
      if (activePlaybackRef.current?.loadKey !== queue.loadKey) return;
      const media = session.getMediaSession?.();
      if (!media) return;
      mediaRef.current = media;
      applyReceiverItem(media);
      setMediaGeneration((value) => value + 1);
    }).catch((error: unknown) => {
      if (loadingQueueKeyRef.current === queue.loadKey) {
        loadingQueueKeyRef.current = null;
      }
      receiverIndexRef.current = null;
      setPlaying(false);
      logFailure("queue load", error);
    });
  }, [activePlayback, applyReceiverItem, castState, selectedAudioTrack, sessionGeneration]);

  useEffect(() => {
    const session = sessionRef.current;
    const framework = (window as CafWindow).cast?.framework;
    if (castState !== "connected" || !session || !framework) return;
    const onMediaSession = (event: { mediaSession: any }) => {
      mediaRef.current = event.mediaSession;
      applyReceiverItem(event.mediaSession);
      setMediaGeneration((value) => value + 1);
    };
    session.addEventListener(framework.SessionEventType.MEDIA_SESSION, onMediaSession);
    const existing = session.getMediaSession?.();
    if (existing) onMediaSession({ mediaSession: existing });
    return () => session.removeEventListener(framework.SessionEventType.MEDIA_SESSION, onMediaSession);
  }, [applyReceiverItem, castState, sessionGeneration]);

  useEffect(() => {
    const media = mediaRef.current ?? sessionRef.current?.getMediaSession?.();
    if (castState !== "connected" || !media) return;
    mediaRef.current = media;
    const update = (alive = true) => {
      if (!alive) return;
      applyReceiverItem(media);
      const state = media.playerState;
      setPlaying(state === "PLAYING" || state === "BUFFERING");
      const estimatedTime = media.getEstimatedTime?.() ?? media.currentTime;
      setCurrentTime(Number.isFinite(estimatedTime) ? estimatedTime : 0);
      const nextDuration = media.media?.duration;
      if (Number.isFinite(nextDuration) && nextDuration > 0) setDuration(nextDuration);
      setMuted(Boolean(media.volume?.muted));
      const queue = activePlaybackRef.current;
      if (
        state === "IDLE" &&
        media.idleReason === "FINISHED" &&
        queue &&
        receiverIndexRef.current === queue.items.length - 1 &&
        queueEndedKeyRef.current !== queue.loadKey
      ) {
        queueEndedKeyRef.current = queue.loadKey;
        if (screenAttachedRef.current) runCallback("onQueueEnded", queue.onQueueEnded);
        else clearActivePlayback();
      }
    };
    media.addUpdateListener(update);
    update(true);
    const poll = window.setInterval(() => update(true), 1000);
    return () => {
      window.clearInterval(poll);
      media.removeUpdateListener(update);
    };
  }, [applyReceiverItem, castState, clearActivePlayback, mediaGeneration]);

  const terminatePlayback = useCallback(() => {
    screenAttachedRef.current = false;
    setPlaying(false);
    clearActivePlayback();
    contextRef.current?.endCurrentSession(true);
  }, [clearActivePlayback]);

  useTerminatePlaybackOnAuthSessionChange(terminatePlayback);

  useEffect(() => {
    if (!playbackSession.revoked) return;
    console.log("[cast:web] terminating revoked playback session");
    terminatePlayback();
  }, [playbackSession.revoked, terminatePlayback]);

  const showCastDialog = useCallback(() => {
    const context = contextRef.current;
    if (!context) return;
    void context.requestSession().catch((error: unknown) => {
      const cancelled = String(error).toLowerCase().includes("cancel");
      if (!cancelled) logFailure("request session", error);
    });
  }, []);

  const togglePlaying = useCallback(() => {
    const media = mediaRef.current;
    if (!media || playbackSession.revoked) return;
    const shouldPause = playing;
    setPlaying(!shouldPause);
    invokeRemote(shouldPause ? "pause" : "play", (ok, fail) =>
      shouldPause ? media.pause(null, ok, fail) : media.play(null, ok, fail)
    );
  }, [playbackSession.revoked, playing]);

  const toggleMuted = useCallback(() => {
    const session = sessionRef.current;
    if (!session?.setMute) return;
    const nextMuted = !muted;
    setMuted(nextMuted);
    void session.setMute(nextMuted).catch((error: unknown) => {
      setMuted(muted);
      logFailure("set receiver muted", error);
    });
  }, [muted]);

  const next = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    setPlaying(true);
    invokeRemote("queue next", (ok, fail) => media.queueNext(ok, fail));
  }, []);

  const previous = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    setPlaying(true);
    invokeRemote("queue previous", (ok, fail) => media.queuePrev(ok, fail));
  }, []);

  const select = useCallback(
    (nextIndex: number) => {
      const queue = activePlaybackRef.current;
      if (!queue || castStateRef.current !== "connected") return;
      const nextQueue = selectPlaybackQueueItem(
        queue,
        nextIndex,
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
      );
      if (!nextQueue) return;
      console.log(
        `[cast:web] direct item selection queue=${queue.id} from=${queue.currentIndex} to=${nextIndex}`
      );
      loadedQueueKeyRef.current = null;
      loadingQueueKeyRef.current = null;
      receiverIndexRef.current = null;
      activePlaybackRef.current = nextQueue;
      setActivePlaybackState(nextQueue);
      setCurrentTime(0);
      setDuration(nextQueue.items[nextIndex]?.duration ?? 0);
      setPlaying(true);
    },
    []
  );

  const seek = useCallback((seconds: number) => {
    const media = mediaRef.current;
    const mediaApi = (window as CafWindow).chrome?.cast?.media;
    if (!media || !mediaApi) return;
    const position = Math.max(0, duration > 0 ? Math.min(seconds, duration) : seconds);
    const request = new mediaApi.SeekRequest();
    request.currentTime = position;
    setCurrentTime(position);
    invokeRemote("seek", (ok, fail) => media.seek(request, ok, fail));
  }, [duration]);

  const setAudioTrack = useCallback((id: string) => {
    preferredAudioTrackRef.current = id;
    const session = sessionRef.current;
    const item = activePlaybackRef.current?.items[
      activePlaybackRef.current.currentIndex
    ];
    const hasTrack = item?.audioTracks.some((track) => track.id === id) ?? false;
    if (!session || !hasTrack) return;
    invokeRemote(`resolve receiver audio track ${id}`, (ok, fail) =>
      session.sendMessage(
        CAST_CONTROL_NAMESPACE,
        { type: "resolve-audio-track", audioTrackId: id },
        ok,
        fail
      )
    );
  }, []);

  useEffect(() => {
    const preferredId = preferredAudioTrackRef.current;
    const active = activePlaybackRef.current;
    if (
      !preferredId ||
      castState !== "connected" ||
      !active ||
      loadedQueueKeyRef.current !== active.loadKey ||
      receiverIndexRef.current !== active.currentIndex ||
      !active.items[active.currentIndex]?.audioTracks.some(
        (track) => track.id === preferredId
      )
    ) {
      return;
    }
    setAudioTrack(preferredId);
  }, [activePlayback?.currentIndex, castState, setAudioTrack]);

  const value = useMemo<CastControllerState>(() => {
    const queue = activePlayback;
    const item = queue?.items[queue.currentIndex] ?? null;
    const progress = duration > 0 && Number.isFinite(currentTime)
      ? Math.max(0, Math.min(currentTime / duration, 1))
      : null;
    return {
      availableAudioTracks,
      castButtonVisible: castState !== "none",
      castState,
      currentTime,
      detachActivePlayback,
      deviceName,
      duration,
      muted,
      next,
      nowPlaying: item && queue && castState === "connected" ? {
          artworkUrl: item.artworkUrl,
          hasNext: queue.currentIndex < queue.items.length - 1,
          hasPrevious: queue.currentIndex > 0,
          id: item.id,
          returnPath: queue.returnPathForItem(item.id),
        subtitle: item.subtitle,
        title: item.title
      } : null,
      playing,
      previous,
      progress,
      select,
      seek,
      selectedAudioTrack,
      setAudioTrack,
      setActivePlayback,
      showCastDialog,
      toggleMuted,
      togglePlaying
    };
  }, [activePlayback, availableAudioTracks, castState, currentTime, detachActivePlayback, deviceName, duration, muted, next, playing, previous, seek, select, selectedAudioTrack, setActivePlayback, setAudioTrack, showCastDialog, toggleMuted, togglePlaying]);

  return <CastControllerContext.Provider value={value}>{children}</CastControllerContext.Provider>;
}

export function useCastController() {
  const context = useContext(CastControllerContext);
  if (!context) throw new Error("useCastController must be used inside CastControllerProvider");
  return context;
}
